import React, { useState } from 'react';
import { useMentorList } from '@/hooks/queries/mentor/useList';
import { useMentorCreate, useMentorUpdate, useMentorDelete } from '@/hooks/mutations/mentor';
import { Mentor, MentorInput } from '@/types/mentors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Search, Edit2, Trash2, User } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { getMediaUrl } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const MentorsPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null);

  const [formData, setFormData] = useState<MentorInput>({
    photo: '',
    decorImage: '',
    translations: [
      { locale: 'uz', fullname: '', position: '', about: '' },
      { locale: 'ru', fullname: '', position: '', about: '' },
      { locale: 'en', fullname: '', position: '', about: '' },
    ],
  });

  const { data: mentorsData, isLoading, refetch } = useMentorList({ pageNumber: page, pageSize: 10, search });
  const { mutate: createMentor, isPending: isCreating } = useMentorCreate();
  const { mutate: updateMentor, isPending: isUpdating } = useMentorUpdate();
  const { mutate: deleteMentor, isPending: isDeleting } = useMentorDelete();

  const handleOpenCreate = () => {
    setSelectedMentor(null);
    setFormData({
      photo: '',
      decorImage: '',
      translations: [
        { locale: 'uz', fullname: '', position: '', about: '' },
        { locale: 'ru', fullname: '', position: '', about: '' },
        { locale: 'en', fullname: '', position: '', about: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (mentor: Mentor) => {
    setSelectedMentor(mentor);
    setFormData({
      photo: mentor.photo || '',
      decorImage: mentor.decorImage || '',
      translations: [
        { locale: 'uz', fullname: mentor.fullname || '', position: mentor.position || '', about: mentor.about || '' },
        { locale: 'ru', fullname: '', position: '', about: '' },
        { locale: 'en', fullname: '', position: '', about: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'fullname' | 'position' | 'about', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedMentor) {
      updateMentor(
        { id: selectedMentor.id, data: formData },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Mentor yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Mentor yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createMentor(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi mentor yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Mentor yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedMentor) return;
    deleteMentor(selectedMentor.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Mentor o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedMentor(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Mentor o'chirilmadi", variant: 'destructive' }),
    });
  };

  const mentorsList: Mentor[] = mentorsData?.data?.data || mentorsData?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Mentorlar</h1>
          <p className="text-sm text-slate-500 mt-1">Platformadagi barcha ustoz va mentorlar ro'yxati</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi mentor
        </Button>
      </div>

      {/* Card Table */}
      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {/* Search */}
        <div className="flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3.5 top-3 size-4 text-slate-400" />
            <Input
              placeholder="Ism yoki lavozim bo'yicha qidirish..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 h-10 bg-slate-50/50 border-slate-200 rounded-xl"
            />
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : mentorsList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <User className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha mentorlar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Rasm</th>
                  <th className="px-4 py-3.5">F.I.SH</th>
                  <th className="px-4 py-3.5">Lavozim</th>
                  <th className="px-4 py-3.5">Haqida</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {mentorsList.map((mentor) => (
                  <tr key={mentor.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      {mentor.photo ? (
                        <img src={getMediaUrl(mentor.photo)} alt={mentor.fullname} className="size-10 rounded-full object-cover border border-slate-200" />
                      ) : (
                        <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                          {mentor.fullname?.[0] || 'M'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{mentor.fullname || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{mentor.position || '—'}</td>
                    <td className="px-4 py-3 max-w-xs truncate text-slate-500">{mentor.about || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(mentor)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedMentor(mentor);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={mentorsData?.meta?.pagination ?? mentorsData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedMentor ? 'Mentorni tahrirlash' : 'Yangi mentor yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileUploadInput
                label="Asosiy Rasm (Photo)"
                value={formData.photo}
                onChange={(url) => setFormData({ ...formData, photo: url })}
              />
              <FileUploadInput
                label="Dekorativ Rasm (Ixtiyoriy)"
                value={formData.decorImage || ''}
                onChange={(url) => setFormData({ ...formData, decorImage: url })}
              />
            </div>

            {/* Translation Tabs */}
            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { fullname: '', position: '', about: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">F.I.SH ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.fullname || ''}
                          onChange={(e) => handleTranslationChange(locale, 'fullname', e.target.value)}
                          placeholder="Ali Valiyev"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Lavozim ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.position || ''}
                          onChange={(e) => handleTranslationChange(locale, 'position', e.target.value)}
                          placeholder="Senior Backend Developer"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Biografiya ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.about || ''}
                          onChange={(v) => handleTranslationChange(locale, 'about', v)}
                          placeholder="10 yillik tajribaga ega dasturchi..."
                          minHeight={80}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Mentorni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu mentor tizimdan butunlay o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default MentorsPage;
